import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { channelsService } from '@/services/channels.service';
import { teamQueryKeys } from '@/lib/queryKeys';
import { useEffectiveTeamId } from '@/hooks/useEffectiveTeamId';
import { toast } from '@/utils/toast';
import { formatApiError } from '@/services/api';

/**
 * useChannels Hook
 *
 * Fetch user's connected social media channels.
 * By default returns only active channels. Pass `{ includeInactive: true }` in Settings
 * to also surface disconnected channels for reconnection.
 *
 * IMPORTANT: All existing callers (no args) keep active-only behavior unchanged.
 */
export function useChannels(opts?: { includeInactive?: boolean }) {
  const { teamId, hasTeam } = useEffectiveTeamId();
  const includeInactive = opts?.includeInactive ?? false;

  return useQuery({
    // Include the flag in the cache key so active-only and all-inclusive datasets
    // are stored separately and don't bleed into each other.
    queryKey: [...teamQueryKeys.channels.all(teamId), { includeInactive }] as const,
    queryFn: () => channelsService.getChannels({ includeInactive }),
    enabled: hasTeam,
    staleTime: 30 * 1000, // 30 seconds - channels change rarely but updates should appear quickly
    retry: 1,
  });
}

/**
 * useChannel Hook
 *
 * Fetch a single channel by ID
 */
export function useChannel(channelId: number) {
  const { teamId, hasTeam } = useEffectiveTeamId();

  return useQuery({
    queryKey: teamQueryKeys.channels.detail(teamId, channelId),
    queryFn: () => channelsService.getChannel(channelId),
    enabled: hasTeam && !!channelId,
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * useDisconnectChannel Hook
 *
 * Disconnect a channel
 */
export function useDisconnectChannel() {
  const queryClient = useQueryClient();
  const { teamId } = useEffectiveTeamId();

  return useMutation({
    mutationFn: (channelId: number) =>
      channelsService.disconnectChannel(channelId),
    onSuccess: () => {
      // Invalidate channels list to refetch
      queryClient.invalidateQueries({ queryKey: teamQueryKeys.channels.all(teamId) });
    },
    onError: (error) => {
      toast.error(formatApiError(error).message);
    },
  });
}

